import React, { useEffect, useState } from "react";
import { api, publicApi } from "@/lib/api";
import { toast } from "sonner";
import { Tag, Check, X, Plus, Trash2, Sparkles, Send, Users, Mail, FileText, Download, Pencil } from "lucide-react";

/** ---------- Manage tab ---------- */
export default function CouponsTab() {
  const [system, setSystem] = useState([]);
  const [seasonal, setSeasonal] = useState([]);
  const [custom, setCustom] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [sendFor, setSendFor] = useState(null); // coupon being broadcast
  const [editFor, setEditFor] = useState(null); // coupon being edited
  const [referrals, setReferrals] = useState(null); // global referral program switch
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/coupons?active_only=false");
      api.get("/referrals/program").then(p => setReferrals(p.data.enabled)).catch(() => {});
      setSystem(r.data.system || []);
      setSeasonal(r.data.seasonal || []);
      setCustom(r.data.custom || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load coupons");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const deactivate = async (code) => {
    if (!window.confirm(`Deactivate "${code}"?`)) return;
    try { await api.delete(`/coupons/${code}`); toast.success("Deactivated"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const toggleActive = async (c) => {
    try {
      const next = c.active === false;
      await api.patch(`/coupons/${c.code}`, { active: next });
      toast.success(next ? `${c.code} is active again` : `${c.code} turned off — no customer can use it at checkout`);
      load();
    }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const ActiveSwitch = ({ c }) => {
    const on = c.active !== false;
    return (
      <button
        type="button"
        role="switch"
        aria-checked={on}
        data-testid={`toggle-active-${c.code}`}
        onClick={() => toggleActive(c)}
        title={on ? "Active — click to turn this coupon off for all customers" : "Inactive — click to re-enable"}
        className="flex items-center gap-2 select-none"
      >
        <span className={`relative w-9 h-[18px] rounded-full transition-colors ${on ? "bg-emerald-500" : "bg-white/15"}`}>
          <span className={`absolute top-0.5 w-3.5 h-3.5 rounded-full bg-[#0A0A0A] transition-all ${on ? "left-[18px]" : "left-0.5"}`}/>
        </span>
        <span className={`text-[9px] uppercase tracking-[0.18em] ${on ? "text-emerald-400" : "text-red-400"}`}>{on ? "Active" : "Inactive"}</span>
      </button>
    );
  };

  return (
    <section className="pb-20 px-6 md:px-12">
      <div className="max-w-[1400px] mx-auto">
        <div className="flex justify-between items-end mb-6">
          <div>
            <h2 className="font-serif text-2xl">Coupons &amp; Promo Codes</h2>
            <p className="text-xs text-[#A1A1AA] mt-1">System coupons auto-apply by customer history. Custom event coupons can be created for weddings, prom, game day, etc.</p>
          </div>
          <button
            data-testid="new-coupon-btn"
            onClick={() => setShowNew(true)}
            className="px-4 py-2 text-[11px] uppercase tracking-[0.22em] border border-[#D4AF37] text-[#D4AF37] flex items-center gap-2"
          ><Plus size={12}/> New event coupon</button>
        </div>

        {loading && <div className="text-xs text-[#A1A1AA]">Loading…</div>}

        {/* Referral program kill-switch */}
        {referrals !== null && (
          <div className={`mb-8 border p-4 flex items-center justify-between gap-4 ${referrals ? "border-white/10 bg-[#0A0A0A]" : "border-red-500/30 bg-red-500/5"}`} data-testid="referral-program-card">
            <div>
              <div className="overline mb-1">Referral program</div>
              <div className="text-sm text-[#F5F2EB]">Friends get 15% off their first ride · referrers earn a 10% credit</div>
              <div className="text-xs text-[#A1A1AA] mt-1">{referrals ? "Active — referral codes and credits work at checkout." : "Paused — no referral code or credit can be applied. Codes and earned credits are kept."}</div>
            </div>
            <button
              data-testid="toggle-referral-program"
              onClick={async () => {
                try {
                  const r = await api.put("/referrals/program", { enabled: !referrals });
                  setReferrals(r.data.enabled);
                  toast.success(r.data.enabled ? "Referral program is active again" : "Referral program paused");
                } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
              }}
              className={`px-4 py-2 text-[10px] uppercase tracking-[0.2em] border ${referrals ? "border-red-500/40 text-red-400" : "border-emerald-500/40 text-emerald-400"}`}
            >{referrals ? "Pause program" : "Resume program"}</button>
          </div>
        )}

        {/* System rules */}
        <div className="mb-8">
          <div className="overline mb-3">Auto-eligible system rules</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {system.map(c => (
              <div key={c.code} className={`border border-white/10 bg-[#0A0A0A] p-4 ${c.active === false ? "opacity-60" : ""}`} data-testid={`system-coupon-${c.code}`}>
                <div className="flex items-start justify-between mb-2">
                  <div className="font-serif text-lg text-[#D4AF37]">{c.code}</div>
                  <div className="flex items-center gap-1.5">
                    {c.active === false && (
                      <div className="text-[10px] uppercase tracking-[0.18em] text-red-400 border border-red-500/40 px-2 py-0.5" data-testid={`system-off-${c.code}`}>Off</div>
                    )}
                    <div className="text-[10px] uppercase tracking-[0.18em] text-[#71717A] border border-white/10 px-2 py-0.5">{c.discount_pct}% off</div>
                  </div>
                </div>
                <div className="text-sm text-[#F5F2EB]">{c.label}</div>
                <div className="text-xs text-[#71717A] mt-2">{c.description}</div>
                <div className="flex justify-between items-center mt-3">
                  <div className="flex items-center gap-3">
                    <ActiveSwitch c={c}/>
                    <div className="text-[10px] text-[#A1A1AA] uppercase tracking-[0.18em]">Auto rule</div>
                  </div>
                  <div className="flex items-center gap-3">
                    <button
                      data-testid={`edit-${c.code}`}
                      onClick={() => setEditFor(c)}
                      className="text-[10px] uppercase tracking-[0.18em] text-[#A1A1AA] flex items-center gap-1 hover:text-[#D4AF37]"
                    ><Pencil size={10}/> Edit</button>
                    <button
                      data-testid={`send-${c.code}`}
                      onClick={() => setSendFor(c)}
                      className="text-[10px] uppercase tracking-[0.18em] text-[#D4AF37] flex items-center gap-1 hover:opacity-80"
                    ><Send size={10}/> Send</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Seasonal & event specials — recurring yearly windows, auto-suggested in season */}
        <div className="overline mb-3">Seasonal & event specials</div>
        <div className="mb-8">
          <div className="grid md:grid-cols-3 gap-3" data-testid="seasonal-coupon-grid">
            {seasonal.map(c => (
              <div key={c.code} className={`border border-white/10 bg-[#0A0A0A] p-4 ${c.active === false ? "opacity-60" : ""}`} data-testid={`seasonal-coupon-${c.code}`}>
                <div className="flex items-start justify-between mb-2">
                  <div className="font-serif text-lg text-[#D4AF37]">{c.code}</div>
                  <div className="flex items-center gap-1.5">
                    {c.active === false ? (
                      <div className="text-[10px] uppercase tracking-[0.18em] text-red-400 border border-red-500/40 px-2 py-0.5" data-testid={`seasonal-off-${c.code}`}>Off</div>
                    ) : c.in_season ? (
                      <div className="text-[10px] uppercase tracking-[0.18em] text-emerald-400 border border-emerald-500/40 px-2 py-0.5" data-testid={`in-season-${c.code}`}>In season</div>
                    ) : (
                      <div className="text-[10px] uppercase tracking-[0.18em] text-[#71717A] border border-white/10 px-2 py-0.5" data-testid={`off-season-${c.code}`}>Off season</div>
                    )}
                    <div className="text-[10px] uppercase tracking-[0.18em] text-[#71717A] border border-white/10 px-2 py-0.5">{c.discount_pct}% off</div>
                  </div>
                </div>
                <div className="text-sm text-[#F5F2EB]">{c.label}</div>
                <div className="text-xs text-[#71717A] mt-2">{c.description}</div>
                <div className="flex justify-between items-center mt-3">
                  <div className="flex items-center gap-3">
                    <ActiveSwitch c={c}/>
                    <div className="text-[10px] text-[#A1A1AA] uppercase tracking-[0.18em]">{c.window_label} · yearly</div>
                  </div>
                  <div className="flex items-center gap-3">
                    <button
                      data-testid={`edit-${c.code}`}
                      onClick={() => setEditFor(c)}
                      className="text-[10px] uppercase tracking-[0.18em] text-[#A1A1AA] flex items-center gap-1 hover:text-[#D4AF37]"
                    ><Pencil size={10}/> Edit</button>
                    <button
                      data-testid={`send-${c.code}`}
                      onClick={() => setSendFor(c)}
                      className="text-[10px] uppercase tracking-[0.18em] text-[#D4AF37] flex items-center gap-1 hover:opacity-80"
                    ><Send size={10}/> Send</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Custom event coupons */}
        <div className="overline mb-3">Custom event coupons</div>
        {custom.length === 0 && (
          <div className="border border-white/10 bg-[#0A0A0A] p-8 text-sm text-[#A1A1AA] text-center">
            No custom coupons yet. Click <span className="text-[#D4AF37]">New event coupon</span> to create one for a wedding, prom, game day, etc.
          </div>
        )}
        {custom.length > 0 && (
          <div className="border border-white/10 bg-[#0A0A0A]" data-testid="custom-coupon-list">
            <div className="grid grid-cols-12 px-4 py-3 text-[10px] uppercase tracking-[0.22em] text-[#71717A] border-b border-white/5">
              <div className="col-span-2">Code</div>
              <div className="col-span-3">Label</div>
              <div className="col-span-1 text-right">Off</div>
              <div className="col-span-2">Event</div>
              <div className="col-span-2">Valid</div>
              <div className="col-span-1 text-right">Uses</div>
              <div className="col-span-1 text-right">Actions</div>
            </div>
            {custom.map(c => (
              <div key={c.code} className="grid grid-cols-12 px-4 py-3 border-b border-white/5 last:border-b-0 items-center text-sm" data-testid={`custom-coupon-${c.code}`}>
                <div className="col-span-2 text-[#D4AF37] font-mono">{c.code}</div>
                <div className="col-span-3 text-[#F5F2EB]">{c.label}</div>
                <div className="col-span-1 text-right text-[#D4AF37]">{c.discount_pct}%</div>
                <div className="col-span-2 text-[#A1A1AA] text-xs uppercase tracking-[0.18em]">{c.event_type}</div>
                <div className="col-span-2 text-[#A1A1AA] text-xs">
                  {c.valid_from || "—"}{c.valid_until ? ` → ${c.valid_until}` : ""}
                </div>
                <div className="col-span-1 text-right text-[#F5F2EB] text-xs">
                  {c.uses_count || 0}{c.max_uses ? `/${c.max_uses}` : ""}
                </div>
                <div className="col-span-1 text-right flex justify-end gap-1">
                  <button
                    data-testid={`edit-${c.code}`}
                    onClick={() => setEditFor(c)}
                    title="Edit coupon"
                    className="p-1.5 text-[#A1A1AA] hover:text-[#D4AF37]"
                  ><Pencil size={14}/></button>
                  <button
                    data-testid={`send-${c.code}`}
                    onClick={() => setSendFor(c)}
                    title="Send to customers"
                    className="p-1.5 text-[#D4AF37]"
                  ><Send size={14}/></button>
                  <button
                    data-testid={`toggle-${c.code}`}
                    onClick={() => toggleActive(c)}
                    title={c.active ? "Deactivate" : "Activate"}
                    className={`p-1.5 ${c.active ? "text-green-400" : "text-[#71717A]"}`}
                  >{c.active ? <Check size={14}/> : <X size={14}/>}</button>
                  <button
                    data-testid={`del-${c.code}`}
                    onClick={() => deactivate(c.code)}
                    className="p-1.5 text-red-400"
                  ><Trash2 size={14}/></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showNew && <NewCouponModal onClose={() => setShowNew(false)} onCreated={() => { setShowNew(false); load(); }}/>}
      {editFor && <EditCouponModal coupon={editFor} onClose={() => setEditFor(null)} onSaved={() => { setEditFor(null); load(); }}/>}
      {sendFor && <SendCouponModal coupon={sendFor} onClose={() => setSendFor(null)}/>}
    </section>
  );
}


/** ---------- Edit modal — system rules get %, label, description + on/off;
     custom event coupons additionally get event type, validity dates and max uses ---------- */
function EditCouponModal({ coupon, onClose, onSaved }) {
  // System rules AND seasonal specials expose the limited field set
  // (label / % / description / active) — their logic/window is automatic.
  const isSystem = !!coupon.system || !!coupon.seasonal;
  const [f, setF] = useState({
    label: coupon.label || "",
    discount_pct: coupon.discount_pct ?? 10,
    description: coupon.description || "",
    active: coupon.active !== false,
    event_type: coupon.event_type || "other",
    valid_from: coupon.valid_from || "",
    valid_until: coupon.valid_until || "",
    max_uses: coupon.max_uses ?? "",
  });
  const [busy, setBusy] = useState(false);
  const upd = (k, v) => setF(p => ({ ...p, [k]: v }));

  const save = async () => {
    if (!f.label || !f.discount_pct) { toast.error("Label and discount % are required"); return; }
    const pct = Number(f.discount_pct);
    if (!(pct > 0 && pct <= 100)) { toast.error("Discount must be between 1 and 100%"); return; }
    setBusy(true);
    try {
      const body = { label: f.label, discount_pct: pct, description: f.description || null, active: f.active };
      if (!isSystem) {
        body.event_type = f.event_type;
        body.valid_from = f.valid_from || null;
        body.valid_until = f.valid_until || null;
        body.max_uses = f.max_uses === "" ? null : Number(f.max_uses);
      }
      await api.patch(`/coupons/${coupon.code}`, body);
      toast.success(`${coupon.code} updated`);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="edit-coupon-modal">
      <div className="bg-[#0A0A0A] border border-white/10 max-w-lg w-full">
        <div className="px-6 py-5 border-b border-white/5 flex justify-between items-center">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#71717A]">{isSystem ? "Edit auto rule" : "Edit coupon"}</div>
            <div className="font-serif text-2xl text-[#D4AF37] mt-1">{coupon.code}</div>
            {isSystem && (
              <div className="text-[10px] text-[#71717A] mt-1 uppercase tracking-[0.18em]">
                {coupon.seasonal ? `Season window (${coupon.window_label}) is automatic — adjust the reward here` : "Eligibility logic is automatic — adjust the reward here"}
              </div>
            )}
          </div>
          <button onClick={onClose} className="text-[#A1A1AA] hover:text-white" data-testid="close-edit-coupon"><X size={18}/></button>
        </div>
        <div className="px-6 py-5 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="field-label">Label *</label>
              <input data-testid="ec-label" className="field-input" value={f.label} onChange={e => upd("label", e.target.value)}/>
            </div>
            <div>
              <label className="field-label">Discount % *</label>
              <input data-testid="ec-pct" type="number" min="1" max="100" className="field-input"
                value={f.discount_pct} onChange={e => upd("discount_pct", e.target.value)}/>
            </div>
          </div>
          {!isSystem && (
            <>
              <div>
                <label className="field-label">Event type</label>
                <select data-testid="ec-type" className="field-input" value={f.event_type} onChange={e => upd("event_type", e.target.value)}>
                  <option value="wedding">Wedding</option>
                  <option value="prom">Prom</option>
                  <option value="game_day">Game day</option>
                  <option value="holiday">Holiday</option>
                  <option value="promo">Promo</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="field-label">Valid from</label>
                  <input data-testid="ec-from" type="date" className="field-input" value={f.valid_from} onChange={e => upd("valid_from", e.target.value)}/>
                </div>
                <div>
                  <label className="field-label">Valid until</label>
                  <input data-testid="ec-until" type="date" className="field-input" value={f.valid_until} onChange={e => upd("valid_until", e.target.value)}/>
                </div>
              </div>
              <div>
                <label className="field-label">Max uses (blank = unlimited)</label>
                <input data-testid="ec-max" type="number" className="field-input" value={f.max_uses} onChange={e => upd("max_uses", e.target.value)}/>
              </div>
            </>
          )}
          <div>
            <label className="field-label">Description</label>
            <textarea data-testid="ec-desc" className="field-input" rows={2} value={f.description} onChange={e => upd("description", e.target.value)}/>
          </div>
          <label className="flex items-center gap-3 cursor-pointer pt-1" data-testid="ec-active-row">
            <input data-testid="ec-active" type="checkbox" checked={f.active} onChange={e => upd("active", e.target.checked)} className="accent-[#D4AF37]"/>
            <span className="text-sm text-[#F5F2EB]">Active {isSystem ? "— auto-applies when a customer qualifies" : ""}</span>
          </label>
        </div>
        <div className="px-6 py-4 border-t border-white/5 flex justify-end gap-2">
          <button data-testid="ec-cancel" onClick={onClose} className="btn-ghost text-[11px]">Cancel</button>
          <button data-testid="edit-coupon-save" disabled={busy} onClick={save} className="btn-gold text-[11px]">
            {busy ? "Saving…" : "Save changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

function NewCouponModal({ onClose, onCreated }) {
  const [f, setF] = useState({
    code: "", label: "", discount_pct: 10, event_type: "wedding",
    description: "", valid_from: "", valid_until: "", max_uses: "",
  });
  const [busy, setBusy] = useState(false);
  const upd = (k, v) => setF(p => ({ ...p, [k]: v }));

  const create = async () => {
    if (!f.code || !f.label || !f.discount_pct) {
      toast.error("Code, label, and discount % are required");
      return;
    }
    setBusy(true);
    try {
      await api.post("/coupons", {
        code: f.code.toUpperCase().replace(/\s+/g, ""),
        label: f.label,
        discount_pct: Number(f.discount_pct),
        event_type: f.event_type,
        description: f.description || null,
        valid_from: f.valid_from || null,
        valid_until: f.valid_until || null,
        max_uses: f.max_uses ? Number(f.max_uses) : null,
      });
      toast.success(`Coupon ${f.code.toUpperCase()} created`);
      onCreated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="new-coupon-modal">
      <div className="bg-[#0A0A0A] border border-white/10 max-w-lg w-full">
        <div className="px-6 py-5 border-b border-white/5 flex justify-between items-center">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#71717A]">New</div>
            <div className="font-serif text-2xl text-[#D4AF37] mt-1">Event coupon</div>
          </div>
          <button onClick={onClose} className="text-[#A1A1AA] hover:text-white" data-testid="close-new-coupon"><X size={18}/></button>
        </div>
        <div className="px-6 py-5 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="field-label">Code *</label>
              <input data-testid="cp-code" className="field-input uppercase" placeholder="WEDDING25"
                value={f.code} onChange={e => upd("code", e.target.value)}/>
            </div>
            <div>
              <label className="field-label">Discount % *</label>
              <input data-testid="cp-pct" type="number" min="1" max="100" className="field-input"
                value={f.discount_pct} onChange={e => upd("discount_pct", e.target.value)}/>
            </div>
          </div>
          <div>
            <label className="field-label">Label *</label>
            <input data-testid="cp-label" className="field-input" placeholder="Wedding Special"
              value={f.label} onChange={e => upd("label", e.target.value)}/>
          </div>
          <div>
            <label className="field-label">Event type</label>
            <select data-testid="cp-type" className="field-input"
              value={f.event_type} onChange={e => upd("event_type", e.target.value)}>
              <option value="wedding">Wedding</option>
              <option value="prom">Prom</option>
              <option value="game_day">Game day</option>
              <option value="holiday">Holiday</option>
              <option value="promo">Promo</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="field-label">Valid from</label>
              <input data-testid="cp-from" type="date" className="field-input"
                value={f.valid_from} onChange={e => upd("valid_from", e.target.value)}/>
            </div>
            <div>
              <label className="field-label">Valid until</label>
              <input data-testid="cp-until" type="date" className="field-input"
                value={f.valid_until} onChange={e => upd("valid_until", e.target.value)}/>
            </div>
          </div>
          <div>
            <label className="field-label">Max uses (optional)</label>
            <input data-testid="cp-max" type="number" className="field-input" placeholder="Unlimited"
              value={f.max_uses} onChange={e => upd("max_uses", e.target.value)}/>
          </div>
          <div>
            <label className="field-label">Description (shown to dispatcher)</label>
            <textarea data-testid="cp-desc" className="field-input" rows={2}
              value={f.description} onChange={e => upd("description", e.target.value)}/>
          </div>
        </div>
        <div className="px-6 py-4 border-t border-white/5 flex justify-end gap-2">
          <button data-testid="cp-cancel" onClick={onClose} className="btn-ghost text-[11px]">Cancel</button>
          <button data-testid="new-coupon-submit" disabled={busy} onClick={create} className="btn-gold text-[11px]">
            {busy ? "Creating…" : "Create coupon"}
          </button>
        </div>
      </div>
    </div>
  );
}


/** ---------- Send Coupon Modal ---------- */
function SendCouponModal({ coupon, onClose }) {
  const [mode, setMode] = useState("segment"); // segment | recipients
  const [segment, setSegment] = useState("loyal_2plus");
  const [segments, setSegments] = useState(null);
  const [recipients, setRecipients] = useState("");
  const [subject, setSubject] = useState(`A gift from Delux Limo — ${coupon.discount_pct}% off with code ${coupon.code}`);
  const [intro, setIntro] = useState("We thought you'd appreciate this — a little something to make your next ride even better.");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.get("/coupons/segments").then(r => setSegments(r.data)).catch(() => {});
  }, []);

  const send = async () => {
    setBusy(true); setResult(null);
    try {
      const body = { code: coupon.code, subject, intro_message: intro };
      if (mode === "recipients") {
        const emails = recipients.split(/[\s,;]+/).map(e => e.trim()).filter(e => e.includes("@"));
        if (!emails.length) { toast.error("Add at least one email"); setBusy(false); return; }
        body.recipients = emails;
      } else {
        body.segment = segment;
      }
      const r = await api.post("/coupons/send", body);
      setResult(r.data);
      toast.success(`${r.data.is_mock ? "Logged (MOCKED)" : "Sent"}: ${r.data.sent}/${r.data.attempted}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send");
    } finally {
      setBusy(false);
    }
  };

  const segLabels = {
    all: "All customers (1+ trip)",
    loyal_2plus: "Loyal customers (2+ trips)",
    loyal_10plus: "Top tier (10+ trips)",
    inactive_90d: "Win-back (90+ days inactive)",
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto" data-testid="send-coupon-modal">
      <div className="bg-[#0A0A0A] border border-white/10 max-w-2xl w-full my-auto">
        <div className="px-6 py-5 border-b border-white/5 flex justify-between items-start">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#71717A]">Send promo</div>
            <div className="font-serif text-2xl text-[#D4AF37] mt-1">{coupon.code}</div>
            <div className="text-xs text-[#A1A1AA] mt-1">{coupon.label} — {coupon.discount_pct}% off</div>
          </div>
          <button onClick={onClose} className="text-[#A1A1AA] hover:text-white" data-testid="close-send"><X size={18}/></button>
        </div>

        {!result && (
          <div className="px-6 py-5 space-y-5">
            <div className="flex gap-2">
              <button
                data-testid="send-mode-segment"
                onClick={() => setMode("segment")}
                className={`px-4 py-2 text-[11px] uppercase tracking-[0.22em] border flex items-center gap-2 ${mode === "segment" ? "border-[#D4AF37] text-[#D4AF37]" : "border-white/10 text-[#A1A1AA]"}`}
              ><Users size={12}/> Segment</button>
              <button
                data-testid="send-mode-recipients"
                onClick={() => setMode("recipients")}
                className={`px-4 py-2 text-[11px] uppercase tracking-[0.22em] border flex items-center gap-2 ${mode === "recipients" ? "border-[#D4AF37] text-[#D4AF37]" : "border-white/10 text-[#A1A1AA]"}`}
              ><Mail size={12}/> Specific emails</button>
            </div>

            {mode === "segment" && (
              <div className="space-y-2">
                {Object.entries(segLabels).map(([k, lbl]) => (
                  <label
                    key={k}
                    data-testid={`segment-${k}`}
                    className={`flex items-center justify-between border p-3 cursor-pointer ${segment === k ? "border-[#D4AF37] bg-[#D4AF37]/5" : "border-white/10"}`}
                  >
                    <div className="flex items-center gap-3">
                      <input type="radio" name="seg" checked={segment === k} onChange={() => setSegment(k)} className="accent-[#D4AF37]"/>
                      <div>
                        <div className="text-sm text-[#F5F2EB]">{lbl}</div>
                        <div className="text-[10px] uppercase tracking-[0.22em] text-[#71717A]">{segments?.[k]?.count ?? "—"} recipients</div>
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            )}

            {mode === "recipients" && (
              <div>
                <label className="field-label">Email addresses (comma, space or newline separated)</label>
                <textarea
                  data-testid="send-recipients"
                  rows={4}
                  className="field-input"
                  value={recipients}
                  onChange={e => setRecipients(e.target.value)}
                  placeholder="customer1@example.com, customer2@example.com"
                />
              </div>
            )}

            <div>
              <label className="field-label">Email subject</label>
              <input data-testid="send-subject" className="field-input" value={subject} onChange={e => setSubject(e.target.value)}/>
            </div>
            <div>
              <label className="field-label">Intro message (1–3 sentences)</label>
              <textarea data-testid="send-intro" rows={3} className="field-input" value={intro} onChange={e => setIntro(e.target.value)}/>
            </div>
            <div className="text-[10px] text-[#71717A] uppercase tracking-[0.18em]">
              Email includes a "BOOK NOW" button with the code pre-applied.
            </div>
          </div>
        )}

        {result && (
          <div className="px-6 py-5 space-y-3" data-testid="send-result">
            <div className="border border-[#D4AF37]/40 bg-[#D4AF37]/10 p-4">
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37]">Done</div>
              <div className="font-serif text-2xl text-[#F5F2EB] mt-1">{result.sent} sent · {result.failed} failed</div>
              {result.is_mock && <div className="text-xs text-[#A1A1AA] mt-2">Resend not configured — emails logged (MOCKED).</div>}
            </div>
          </div>
        )}

        <div className="border-t border-white/5 px-6 py-4 flex justify-end gap-2">
          <button onClick={onClose} className="btn-ghost text-[11px]">{result ? "Close" : "Cancel"}</button>
          {!result && (
            <button data-testid="send-confirm" disabled={busy} onClick={send} className="btn-gold text-[11px]">
              <Send size={12}/> {busy ? "Sending…" : "Send"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}


/** ---------- Inline picker for booking forms ---------- */
/**
 * Props: customer_email, value (code), onChange(code, pct, label) — set onChange("","",null) to clear.
 * Auto-fetches eligible coupons when email changes; also allows manual entry + validation.
 */
export function CouponPicker({ customer_email, value, onChange, dataTestidPrefix = "coupon", isPublic = false }) {
  const [eligible, setEligible] = useState([]);
  const [manual, setManual] = useState(value || "");
  const [info, setInfo] = useState(null); // resolved info for current value
  const [checking, setChecking] = useState(false);
  const client = isPublic ? publicApi : api;

  useEffect(() => {
    setManual(value || "");
  }, [value]);

  // Fetch eligible when email changes
  useEffect(() => {
    if (!customer_email || !customer_email.includes("@")) {
      setEligible([]); return;
    }
    let cancel = false;
    client.get(`/coupons/eligible?email=${encodeURIComponent(customer_email)}`)
      .then(r => { if (!cancel) setEligible(r.data.eligible || []); })
      .catch(() => { if (!cancel) setEligible([]); });
    return () => { cancel = true; };
  }, [customer_email]);

  // Re-validate when manual code changes (debounced)
  useEffect(() => {
    if (!manual) { setInfo(null); return; }
    const t = setTimeout(async () => {
      setChecking(true);
      try {
        const r = await client.post("/coupons/validate", { code: manual, customer_email });
        setInfo(r.data);
      } catch {
        setInfo({ valid: false, reason: "Validation failed" });
      } finally {
        setChecking(false);
      }
    }, 350);
    return () => clearTimeout(t);
  }, [manual, customer_email]);

  const apply = (c) => {
    setManual(c.code);
    setInfo({ valid: true, code: c.code, label: c.label, discount_pct: c.discount_pct });
    onChange(c.code, c.discount_pct, c.label);
  };

  const clear = () => {
    setManual("");
    setInfo(null);
    onChange("", 0, null);
  };

  const submitManual = () => {
    if (info?.valid) {
      onChange(info.code, info.discount_pct, info.label);
    } else if (manual) {
      toast.error(info?.reason || "Coupon not valid");
    }
  };

  return (
    <div className="space-y-3" data-testid={`${dataTestidPrefix}-picker`}>
      {/* Auto-suggest pills */}
      {eligible.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#71717A] mb-2 flex items-center gap-2">
            <Sparkles size={11} className="text-[#D4AF37]"/> Available for this customer
          </div>
          <div className="flex flex-wrap gap-2">
            {eligible.map(c => {
              const active = (value || "").toUpperCase() === c.code;
              return (
                <button
                  type="button"
                  key={c.code}
                  data-testid={`${dataTestidPrefix}-suggest-${c.code}`}
                  onClick={() => active ? clear() : apply(c)}
                  className={`flex items-center gap-2 px-3 py-2 text-xs border transition ${
                    active ? "border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37]" :
                            "border-white/10 text-[#A1A1AA] hover:border-white/30"
                  }`}
                >
                  <Tag size={11}/>
                  <span className="font-mono">{c.code}</span>
                  <span className="text-[#D4AF37]">{c.discount_pct}% off</span>
                  {active && <Check size={11}/>}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Manual code entry */}
      <div className="flex gap-2">
        <input
          data-testid={`${dataTestidPrefix}-input`}
          className="field-input flex-1 uppercase"
          placeholder="Promo / event code (e.g. WEDDING25)"
          value={manual}
          onChange={e => setManual(e.target.value.toUpperCase())}
          onBlur={submitManual}
          onKeyDown={e => e.key === "Enter" && submitManual()}
        />
        {value && (
          <button
            type="button"
            data-testid={`${dataTestidPrefix}-clear`}
            onClick={clear}
            className="btn-ghost text-[10px] !py-2 !px-3"
          >Clear</button>
        )}
      </div>

      {/* Feedback */}
      {checking && <div className="text-[10px] text-[#71717A] uppercase tracking-[0.18em]">Checking…</div>}
      {info && !checking && (
        info.valid ? (
          <div className="text-xs flex items-center gap-2 text-[#D4AF37]" data-testid={`${dataTestidPrefix}-valid`}>
            <Check size={12}/> {info.label} — {info.discount_pct}% off applied
            {info.overridden && <span className="text-[9px] uppercase tracking-[0.15em] text-amber-300 border border-amber-400/40 px-1.5 py-0.5" data-testid={`${dataTestidPrefix}-override`}>Dispatch override · no limits</span>}
          </div>
        ) : (
          <div className="text-xs text-red-400" data-testid={`${dataTestidPrefix}-invalid`}>
            {info.reason || "Not eligible"}
          </div>
        )
      )}
    </div>
  );
}
